import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { Surface, IconButton, Badge, useTheme } from 'react-native-paper';
import { useSync } from '../hooks/useSync';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { SyncIndicator } from './SyncIndicator';

interface SyncStatusBarProps {
  showDetails?: boolean;
  onPress?: () => void;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const SyncStatusBar: React.FC<SyncStatusBarProps> = ({
  showDetails = false,
  onPress,
  compact = true,
  style
}) => {
  const theme = useTheme();
  const [modalVisible, setModalVisible] = useState(false);
  const spinValue = new Animated.Value(0);

  const {
    isOnline: legacyIsOnline,
    pendingActionsCount,
    syncInProgress: legacySyncInProgress,
    getSyncStatusText,
    forceSync: legacyForceSync,
  } = useSync();

  // Get offline review service stats (includes pending responses from SQLite)
  const offlineSync = useOfflineSync();

  // Use offlineSync values on mobile (includes both systems), legacy values on web
  const isOnline = Platform.OS !== 'web' ? offlineSync.isOnline : legacyIsOnline;
  const syncInProgress = Platform.OS !== 'web' ? offlineSync.syncInProgress : legacySyncInProgress;
  const totalPendingCount = Platform.OS !== 'web' ? offlineSync.totalPending : pendingActionsCount;
  const waitingToSyncCount = Platform.OS !== 'web' ? offlineSync.waitingToSyncCount ?? totalPendingCount : pendingActionsCount;
  const retryNeededCount = Platform.OS !== 'web' ? offlineSync.retryNeededCount ?? offlineSync.failedResponses ?? 0 : 0;
  const hasFailedItems = Platform.OS !== 'web' && retryNeededCount > 0;
  const recoveryAvailable = Platform.OS !== 'web' && offlineSync.recoveryAvailable;

  // Use enhanced forceSync that triggers both systems
  const forceSync = Platform.OS !== 'web' ? offlineSync.forceSync : legacyForceSync;

  // Animation for sync icon
  useEffect(() => {
    let animationLoop: Animated.CompositeAnimation | null = null;

    if (syncInProgress) {
      animationLoop = Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        })
      );
      animationLoop.start();
    } else {
      spinValue.setValue(0);
    }

    return () => {
      if (animationLoop) {
        animationLoop.stop();
      }
    };
  }, [syncInProgress]);

  const getStatusIcon = () => {
    if (!isOnline) return 'wifi-off';
    if (syncInProgress) return 'sync';
    if (hasFailedItems) return 'alert-circle';
    if (waitingToSyncCount > 0) return 'clock-outline';
    if (recoveryAvailable) return 'lifebuoy';
    return 'check-circle';
  };

  const getStatusColor = () => {
    if (!isOnline) return theme.colors.error;
    if (hasFailedItems) return theme.colors.error;
    if (syncInProgress) return theme.colors.primary;
    if (waitingToSyncCount > 0) return theme.colors.onSurfaceVariant;
    if (recoveryAvailable) return theme.colors.error;
    return theme.colors.primary;
  };

  const getPendingSummary = () => {
    const parts = [];
    if (offlineSync.pendingResponses > 0) {
      parts.push(`${offlineSync.pendingResponses} response${offlineSync.pendingResponses !== 1 ? 's' : ''}`);
    }
    if (offlineSync.pendingPhotos > 0) {
      parts.push(`${offlineSync.pendingPhotos} photo${offlineSync.pendingPhotos !== 1 ? 's' : ''}`);
    }
    if (offlineSync.pendingOnDemandItems > 0) {
      parts.push(`${offlineSync.pendingOnDemandItems} custom item${offlineSync.pendingOnDemandItems !== 1 ? 's' : ''}`);
    }
    if (offlineSync.pendingReviews > 0) {
      parts.push(`${offlineSync.pendingReviews} review${offlineSync.pendingReviews !== 1 ? 's' : ''}`);
    }
    if (pendingActionsCount > 0) {
      parts.push(`${pendingActionsCount} action${pendingActionsCount !== 1 ? 's' : ''}`);
    }
    return parts.length > 0
      ? parts.join(', ')
      : `${totalPendingCount} item${totalPendingCount !== 1 ? 's' : ''}`;
  };

  const getChoiceSummary = () =>
    `${offlineSync.recoverableItems} saved item${offlineSync.recoverableItems !== 1 ? 's' : ''} need a choice`;

  const getPendingDetailText = () => {
    if (totalPendingCount === 0) {
      return null;
    }
    if (!isOnline) {
      return 'Will sync when back online';
    }
    if (hasFailedItems) {
      if (waitingToSyncCount > 0) {
        return `${waitingToSyncCount} waiting, ${retryNeededCount} need retry`;
      }
      return `${retryNeededCount} item${retryNeededCount !== 1 ? 's' : ''} need retry`;
    }
    return 'Waiting for next sync';
  };

  const getEnhancedStatusText = () => {
    if (Platform.OS === 'web') {
      return getSyncStatusText();
    }

    if (syncInProgress) {
      return 'Syncing...';
    }

    if (!isOnline) {
      return totalPendingCount > 0
        ? `Offline - ${totalPendingCount} item${totalPendingCount !== 1 ? 's' : ''} waiting to sync`
        : 'Offline';
    }

    if (hasFailedItems) {
      if (recoveryAvailable) {
        return waitingToSyncCount > 0
          ? `${waitingToSyncCount} waiting, ${retryNeededCount} need retry, ${getChoiceSummary()}`
          : `${retryNeededCount} need retry, ${getChoiceSummary()}`;
      }
      return `${retryNeededCount} item${retryNeededCount !== 1 ? 's' : ''} need retry`;
    }

    if (waitingToSyncCount > 0) {
      if (recoveryAvailable) {
        return `${getPendingSummary()} waiting, ${getChoiceSummary()}`;
      }
      return `Waiting to sync: ${getPendingSummary()}`;
    }

    if (recoveryAvailable) {
      return `Saved changes need a choice - ${offlineSync.recoverableItems} item${offlineSync.recoverableItems !== 1 ? 's' : ''}`;
    }

    return 'All synced';
  };

  const handlePress = () => {
    if (onPress) {
      onPress();
    } else {
      setModalVisible(true);
    }
  };

  const handleModalDismiss = () => {
    setModalVisible(false);
  };

  // Hide when everything is synced and not explicitly shown
  if (!showDetails && isOnline && totalPendingCount === 0 && !syncInProgress && !hasFailedItems && !recoveryAvailable) {
    return null;
  }

  // Compact mode for header integration
  if (compact) {
    return (
      <>
        <TouchableOpacity
          testID="sync-status-bar"
          style={[styles.compactContainer, style]}
          onPress={handlePress}
          activeOpacity={0.7}
        >
          <View style={styles.compactContent}>
            <Animated.View
              style={[
                styles.compactIconContainer,
                {
                  transform: [{
                    rotate: spinValue.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    })
                  }]
                }
              ]}
            >
              <IconButton
                icon={getStatusIcon()}
                iconColor={getStatusColor()}
                size={18}
                style={styles.compactIcon}
              />
            </Animated.View>

            {totalPendingCount > 0 && (
              <Badge
                style={[
                  styles.badge,
                  { backgroundColor: hasFailedItems ? theme.colors.error : theme.colors.primary }
                ]}
                size={16}
              >
                {totalPendingCount > 99 ? '99+' : totalPendingCount}
              </Badge>
            )}
          </View>
        </TouchableOpacity>

        <SyncIndicator
          visible={modalVisible}
          onDismiss={handleModalDismiss}
          compact={false}
        />
      </>
    );
  }

  // Full size mode
  return (
    <>
      <Surface
        testID="sync-status-bar"
        style={[
          styles.container,
          { backgroundColor: theme.colors.surfaceVariant },
          style
        ]}
        elevation={1}
      >
        <TouchableOpacity
          style={styles.touchableContent}
          onPress={handlePress}
          activeOpacity={0.7}
        >
          <View style={styles.content}>
            <Animated.View
              style={[
                styles.iconContainer,
                {
                  transform: [{
                    rotate: spinValue.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '360deg'],
                    })
                  }]
                }
              ]}
            >
              <IconButton
                icon={getStatusIcon()}
                iconColor={getStatusColor()}
                size={20}
                style={styles.statusIcon}
              />
            </Animated.View>

            <View style={styles.textContainer}>
              <Text
                testID="sync-status-text"
                style={[styles.statusText, { color: theme.colors.onSurface }]}
              >
                {getEnhancedStatusText()}
              </Text>
              {totalPendingCount > 0 && (
                <Text style={[styles.pendingText, { color: theme.colors.onSurfaceVariant }]}>
                  {getPendingDetailText()}
                </Text>
              )}
              {hasFailedItems && (
                <Text style={[styles.warningText, { color: theme.colors.error }]}>
                  Tap to retry sync
                </Text>
              )}
            </View>

            {totalPendingCount > 0 && (
              <Badge
                style={[
                  styles.pendingBadge,
                  { backgroundColor: hasFailedItems ? theme.colors.error : theme.colors.primary }
                ]}
              >
                {totalPendingCount > 99 ? '99+' : totalPendingCount}
              </Badge>
            )}
          </View>
        </TouchableOpacity>
      </Surface>

      <SyncIndicator
        visible={modalVisible}
        onDismiss={handleModalDismiss}
        compact={false}
      />
    </>
  );
};

const styles = StyleSheet.create({
  // Compact mode styles
  compactContainer: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  compactContent: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  },
  compactIconContainer: {
    // No additional styles needed
  },
  compactIcon: {
    margin: 0,
  },
  badge: {
    position: 'absolute',
    top: -2,
    right: -2,
    minWidth: 16,
    height: 16,
  },

  // Full size mode styles
  container: {
    borderRadius: 8,
    marginHorizontal: 12,
    marginVertical: 4,
  },
  touchableContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    marginRight: 12,
  },
  statusIcon: {
    margin: 0,
  },
  textContainer: {
    flex: 1,
  },
  statusText: {
    fontSize: 16,
    fontWeight: '600',
  },
  pendingText: {
    fontSize: 14,
    marginTop: 2,
  },
  warningText: {
    fontSize: 13,
    marginTop: 4,
    fontWeight: '600',
  },
  pendingBadge: {
    marginLeft: 12,
  },
});
